import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Save, Shield, Upload, FileText, ArrowRight, ArrowLeft, Plus, Trash2, CheckCircle, Phone, Mail, MapPin, DollarSign, ShieldCheck, AlertTriangle, RotateCcw, User, Heart, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../Config';
import SocioServiciosSection from './socio-components/SocioServiciosSection';
import { TipoContacto, TipoContactoLabels } from '../enums/TipoContacto';

const Field = ({ label, children, className = "" }) => (
  <div className={`enterprise-field group ${className}`}>
    {children}
    <label className="group-focus-within:text-uni-teal group-focus-within:scale-[0.8] group-focus-within:-translate-y-7 transition-all">
      {label}
    </label>
  </div>
);

const inputClass = "enterprise-input peer";

export default function SocioEditar() {
  const { id } = useParams();
  const { user, token, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [socioData, setSocioData] = useState(null);
  const isAdmin = user?.roles?.includes('ADMIN');
  const isActivo = socioData?.estado?.id === 1;

  // --- CATALOGOS ---
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [generos, setGeneros] = useState([]);
  const [estadosCiviles, setEstadosCiviles] = useState([]);
  const [nacionalidades, setNacionalidades] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [tiposSocio, setTiposSocio] = useState([]);
  const [calificacionesRiesgo, setCalificacionesRiesgo] = useState([]);
  const [nivelesFidelidad, setNivelesFidelidad] = useState([]);
  const [tiposPersona] = useState([
    { id: 1, valor: 'FISICA', descripcion: 'Persona Física' },
    { id: 2, valor: 'JURIDICA', descripcion: 'Persona Jurídica' }
  ]);

  const [departamentos, setDepartamentos] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [barrios, setBarrios] = useState([]);
  const [tiposDireccion, setTiposDireccion] = useState([]);
  const [profesiones, setProfesiones] = useState([]);
  const [rangoAntiguedades, setRangoAntiguedades] = useState([]);
  const [tiposIngreso, setTiposIngreso] = useState([]);
  const [parentescos, setParentescos] = useState([]);

  // --- WIZARD STATE (REMOVED: Now Single Page) ---

  // --- FORM DATA ---
  const [formData, setFormData] = useState({
    tipoPersona: { id: 1, valor: 'FISICA' },

    // Paso 1: Personales
    nombres: '', apellidos: '', tipoDocumento: { id: '' }, numeroDocumento: '',
    fechaNacimiento: '', genero: { id: '' }, estadoCivil: { id: '' },
    nacionalidad: { id: '' }, lugarNacimiento: '',

    // Address Data
    departamentoId: '',
    ciudadId: '',
    barrioId: '',
    tipoDireccionId: '',
    nroCasa: '',
    direccionExacta: '',
    referencia: '',

    // Contactos (Static Fields)
    prefijoPrincipal: '+595',
    celularPrincipal: '',
    prefijoSecundario: '+595',
    celularSecundario: '',
    emailContacto: '',

    // Juridica
    razonSocial: '', ruc: '', fechaConstitucion: '', representanteLegal: '',

    // Conyuge (Solo Fisica y Casado)
    conyugeNombre: '', conyugeDocumento: '',

    // Paso 2: Laborales y Económicos
    tipoSocio: { id: '' }, // New
    calificacionRiesgo: { id: '' }, // New (ReadOnly)
    nivelFidelidad: { id: '' }, // New (ReadOnly)
    categoria: { id: '' },
    datosLaborales: { empresa: '', profesionId: '', rangoAntiguedadId: '', direccion: '', telefono: '' },

    // Perfil Economico (Dynamic - replaces ingreso/origen)
    perfilEconomico: [
      { tipoIngresoId: '', montoMensualPromedio: '', descripcionFuente: '', esActual: true }
    ],

    esPep: false,

    // Paso 3: Beneficiarios (Old Step 4)
    beneficiarios: [], // { nombreCompleto, numeroDocumento, parentescoId, porcentaje }
    tutores: [], // { tutorId, parentescoId, esPrincipal, nombreTutor }
    servicios: [],
  });

  // --- ARCHIVOS ---
  const [cedulaFrontal, setCedulaFrontal] = useState(null);
  const [cedulaDorso, setCedulaDorso] = useState(null);
  const [previewFrontal, setPreviewFrontal] = useState(null);
  const [previewDorso, setPreviewDorso] = useState(null);

  // --- PREVIEW MODAL STATE ---
  const [showPreview, setShowPreview] = useState(false);
  const [createdSocioId, setCreatedSocioId] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  // --- EFFECTS ---
  useEffect(() => {
    if (hasPermission('socios.write') || hasPermission('socios.read')) {
      fetchCatalogos().then(() => {
        fetchSocioOriginal();
        fetchDocumentos();
      });
    }
  }, [hasPermission]);

  const fetchDocumentos = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_BASE_URL}/socio/${id}/documentos`, { headers });
      if (res.ok) {
        const docs = await res.json();
        // Ordenar por ID descendente para obtener siempre el último subido
        const sortedDocs = [...docs].sort((a, b) => b.id - a.id);
        const docFrente = sortedDocs.find(d => d.lado === 'Frente');
        const docDorso = sortedDocs.find(d => d.lado === 'Dorso');

        if (docFrente) {
          try {
            const fRes = await fetch(`${API_BASE_URL}/socio/documentos/${docFrente.id}/descargar`, { headers });
            if (fRes.ok) {
              const fBlob = await fRes.blob();
              setPreviewFrontal(URL.createObjectURL(fBlob));
            }
          } catch (e) { console.warn("Previsualización frontal no disponible"); }
        }

        if (docDorso) {
          try {
            const dRes = await fetch(`${API_BASE_URL}/socio/documentos/${docDorso.id}/descargar`, { headers });
            if (dRes.ok) {
              const dBlob = await dRes.blob();
              setPreviewDorso(URL.createObjectURL(dBlob));
            }
          } catch (e) { console.warn("Previsualización dorso no disponible"); }
        }
      }
    } catch (err) {
      console.error("Error al listar documentos:", err);
    }
  };

  const fetchSocioOriginal = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_BASE_URL}/socio/${id}`, { headers });
      if (!res.ok) throw new Error('No se pudo cargar el socio');
      const data = await res.json();
      setSocioData(data);

      const isFisica = data.tipoPersona?.valor === 'FISICA';

      // Mapear Direccion Principal
      const dirPrincipal = data.direcciones?.find(d => d.esPrincipal) || {};

      // Mapear Contactos
      const getContacto = (tipo, esPrincipal) => data.contactos?.find(c => c.tipoContacto === tipo && c.esPrincipal === esPrincipal)?.valor || '';
      const phoneSplit = getContacto('CELULAR', true).split(' ');
      const phoneSecSplit = getContacto('CELULAR', false).split(' ');

      setFormData({
        tipoPersona: { id: data.tipoPersona?.id || 1, valor: data.tipoPersona?.valor || 'FISICA' },

        // Fisica
        nombres: data.personaFisica?.nombres || '',
        apellidos: data.personaFisica?.apellidos || '',
        tipoDocumento: { id: data.tipoDocumento?.id || '' },
        numeroDocumento: data.personaFisica?.numeroDocumento || data.personaJuridica?.ruc || '',
        fechaNacimiento: data.personaFisica?.fechaNacimiento || '',
        genero: { id: data.personaFisica?.genero?.id || '' },
        estadoCivil: { id: data.personaFisica?.estadoCivil?.id || '' },
        nacionalidad: { id: data.personaFisica?.nacionalidad?.id || '' },
        lugarNacimiento: data.personaFisica?.lugarNacimiento || '',
        conyugeNombre: data.personaFisica?.conyugeNombre || '',
        conyugeDocumento: data.personaFisica?.conyugeDocumento || '',

        // Juridica
        razonSocial: data.personaJuridica?.razonSocial || '',
        ruc: data.personaJuridica?.ruc || '',
        fechaConstitucion: data.personaJuridica?.fechaConstitucion || '',
        representanteLegal: data.personaJuridica?.representanteLegal || '',

        // Clasificacion
        tipoSocio: { id: data.tipoSocio?.id || '' },
        calificacionRiesgo: { id: data.calificacionRiesgo?.id || '' },
        nivelFidelidad: { id: data.nivelFidelidad?.id || '' },
        categoria: { id: data.categoria?.id || '' },

        // Direccion
        departamentoId: dirPrincipal.departamentoId || '',
        ciudadId: dirPrincipal.ciudadId || '',
        barrioId: dirPrincipal.barrio?.id || '',
        tipoDireccionId: dirPrincipal.tipoDireccion?.id || '',
        nroCasa: dirPrincipal.nroCasa || '',
        direccionExacta: dirPrincipal.direccionExacta || '',
        referencia: dirPrincipal.referencia || '',

        // Contactos
        prefijoPrincipal: phoneSplit.length > 1 ? phoneSplit[0] : '+595',
        celularPrincipal: phoneSplit.length > 1 ? phoneSplit.slice(1).join(' ') : phoneSplit[0] || '',
        prefijoSecundario: phoneSecSplit.length > 1 ? phoneSecSplit[0] : '+595',
        celularSecundario: phoneSecSplit.length > 1 ? phoneSecSplit.slice(1).join(' ') : phoneSecSplit[0] || '',
        emailContacto: getContacto('EMAIL', false) || data.correoElectronico || '',

        // Otros
        esPep: data.esPep || false,
        datosLaborales: {
          empresa: data.datosLaborales?.empresa || '',
          profesionId: data.datosLaborales?.profesion?.id || '',
          rangoAntiguedadId: data.datosLaborales?.rangoAntiguedad?.id || '',
          direccion: data.datosLaborales?.direccion || '',
          telefono: data.datosLaborales?.telefono || '',
          tipoOcupacion: data.datosLaborales?.tipoOcupacion || ''
        },

        perfilEconomico: data.perfilEconomico?.length > 0 ? data.perfilEconomico.map(pe => ({
          tipoIngresoId: pe.tipoIngreso?.id || '',
          montoMensualPromedio: pe.montoMensualPromedio || '',
          descripcionFuente: pe.descripcionFuente || '',
          esActual: pe.esActual !== false,
          profesionId: pe.profesion?.id || '',
          empresa: pe.empresa || '',
          direccion: pe.direccion || '',
        })) : [{ tipoIngresoId: '', montoMensualPromedio: '', descripcionFuente: '', esActual: true, profesionId: '', empresa: '', direccion: '' }],

        referencias: data.referencias?.length > 0 ? data.referencias.map(r => ({
          nombre: r.nombre || '',
          telefono: r.telefono || '',
          tipoReferencia: r.tipoReferencia || 'PERSONAL'
        })) : [{ nombre: '', telefono: '', tipoReferencia: 'PERSONAL' }],

        beneficiarios: data.beneficiarios?.length > 0 ? data.beneficiarios.map(b => ({
          nombreCompleto: b.nombreCompleto || '',
          numeroDocumento: b.numeroDocumento || '',
          parentescoId: b.parentesco?.id || '',
          porcentaje: b.porcentaje || ''
        })) : [],
        tutores: data.tutores?.length > 0 ? data.tutores.map(t => ({
          id: t.id,
          tutorId: t.tutor?.id || '',
          nombreTutor: t.tutor?.nombreCompleto || (t.tutor?.personaFisica ? `${t.tutor.personaFisica.nombres} ${t.tutor.personaFisica.apellidos}` : 'Socio Desconocido'),
          parentescoId: t.parentesco?.id || '',
          esPrincipal: t.esPrincipal || false
        })) : [],
        servicios: data.servicios || []
      });
      setLoading(false);
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
      navigate('/socios');
    }
  };

  const fetchCatalogos = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const responses = await Promise.all([
        fetch(`${API_BASE_URL}/catalogos/tipos-documento?categoriaValue=IDENT`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/generos`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/estados-civiles`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/nacionalidades`, { headers }),
        fetch(`${API_BASE_URL}/ubicacion/departamentos`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/tipos-direccion`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/tipos-socio`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/calificaciones-riesgo`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/niveles-fidelidad`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/profesiones`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/rangos-antiguedad`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/tipos-ingreso`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/parentescos`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/categorias`, { headers })
      ]);

      if (responses[0].ok) setTiposDocumento(await responses[0].json());
      if (responses[1].ok) setGeneros(await responses[1].json());
      if (responses[2].ok) setEstadosCiviles(await responses[2].json());
      if (responses[3].ok) setNacionalidades(await responses[3].json());
      if (responses[4].ok) setDepartamentos(await responses[4].json());
      if (responses[5].ok) setTiposDireccion(await responses[5].json());

      if (responses[6] && responses[6].ok) {
        const data = await responses[6].json();
        setTiposSocio(data);
      }
      if (responses[7] && responses[7].ok) setCalificacionesRiesgo(await responses[7].json());
      if (responses[8] && responses[8].ok) setNivelesFidelidad(await responses[8].json());
      if (responses[9] && responses[9].ok) setProfesiones(await responses[9].json());
      if (responses[10] && responses[10].ok) setRangoAntiguedades(await responses[10].json());
      if (responses[11] && responses[11].ok) setTiposIngreso(await responses[11].json());
      if (responses[12] && responses[12].ok) setParentescos(await responses[12].json());
      if (responses[13] && responses[13].ok) setCategorias(await responses[13].json());

    } catch (err) { console.error("Error catálogos:", err); }
  };

  // --- CASCADING ADDRESS EFFECTS ---
  useEffect(() => {
    if (formData.departamentoId) {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      fetch(`${API_BASE_URL}/ubicacion/ciudades/${formData.departamentoId}`, { headers })
        .then(res => res.json())
        .then(data => setCiudades(data))
        .catch(err => console.error(err));
      // Reset dependent fields
      // setFormData(prev => ({ ...prev, ciudadId: '', barrioId: '' })); // Causes infinite loop if not careful, better handled in change
    } else {
      setCiudades([]);
    }
  }, [formData.departamentoId, token]);

  useEffect(() => {
    if (formData.ciudadId) {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      fetch(`${API_BASE_URL}/ubicacion/barrios/${formData.ciudadId}`, { headers })
        .then(res => res.json())
        .then(data => setBarrios(data))
        .catch(err => console.error(err));
    } else {
      setBarrios([]);
    }
  }, [formData.ciudadId, token]);

  // --- HANDLERS ---
  const handleInputChange = (e, section = null) => {
    let { name, value, type, checked } = e.target;

    // --- VALIDACIÓN Y FORMATO AUTOMÁTICO ---
    if (name === 'numeroDocumento') {
      value = value.replace(/[^0-9]/g, '');
    }
    if (['nombres', 'apellidos', 'lugarNacimiento'].includes(name)) {
      value = value.toUpperCase();
    }

    if (section === 'datosLaborales') {
      setFormData(prev => ({
        ...prev,
        datosLaborales: { ...prev.datosLaborales, [name]: value }
      }));
    } else if (name === 'tipoPersona') {
      setFormData({ ...formData, tipoPersona: tiposPersona.find(t => t.id === parseInt(value)) });
    } else if (['tipoDocumento', 'genero', 'estadoCivil', 'nacionalidad', 'tipoSocio', 'categoria'].includes(name)) {
      setFormData({ ...formData, [name]: { id: parseInt(value) } });
    } else {
      setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    }
  };



  const addPerfil = () => {
    setFormData(prev => ({
      ...prev,
      perfilEconomico: [...prev.perfilEconomico, { tipoIngresoId: '', montoMensualPromedio: '', descripcionFuente: '', esActual: true, profesionId: '', empresa: '', direccion: '' }]
    }));
  };
  const updatePerfil = (index, field, value) => {
    const updated = [...formData.perfilEconomico];
    updated[index][field] = value;
    setFormData(prev => ({ ...prev, perfilEconomico: updated }));
  };
  const removePerfil = (index) => {
    const updated = [...formData.perfilEconomico];
    updated.splice(index, 1);
    setFormData(prev => ({ ...prev, perfilEconomico: updated }));
  };


  // Referencias Handlers
  const addReferencia = () => {
    setFormData(prev => ({
      ...prev,
      referencias: [...prev.referencias, { nombre: '', telefono: '', tipoReferencia: 'PERSONAL' }]
    }));
  };
  const updateReferencia = (index, field, value) => {
    const updated = [...formData.referencias];
    updated[index][field] = value;
    setFormData({ ...formData, referencias: updated });
  };
  const removeReferencia = (index) => {
    const updated = [...formData.referencias];
    updated.splice(index, 1);
    setFormData({ ...formData, referencias: updated });
  };

  // Beneficiarios Handlers
  const addBeneficiario = () => {
    setFormData(prev => ({
      ...prev,
      beneficiarios: [...prev.beneficiarios, { nombreCompleto: '', numeroDocumento: '', parentescoId: '', porcentaje: '10' }]
    }));
  };
  const updateBeneficiario = (index, field, value) => {
    const updated = [...formData.beneficiarios];
    updated[index][field] = value;
    setFormData({ ...formData, beneficiarios: updated });
  };
  const removeBeneficiario = (index) => {
    const updated = [...formData.beneficiarios];
    updated.splice(index, 1);
    setFormData({ ...formData, beneficiarios: updated });
  };

  // Tutores Handlers
  const addTutor = () => {
    setFormData(prev => ({
      ...prev,
      tutores: [...prev.tutores, { tutorId: '', parentescoId: '', esPrincipal: prev.tutores.length === 0, nombreTutor: '' }]
    }));
  };
  const updateTutor = (index, field, value) => {
    const updated = [...formData.tutores];
    updated[index][field] = value;
    setFormData({ ...formData, tutores: updated });
  };
  const removeTutor = (index) => {
    const updated = [...formData.tutores];
    updated.splice(index, 1);
    setFormData({ ...formData, tutores: updated });
  };

  const buscarTutor = async (index, termino) => {
    if (!termino) return;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_BASE_URL}/socio/buscar?q=${encodeURIComponent(termino)}`, { headers });
      
      if (res.ok) {
        const results = await res.json();
        
        if (results.length === 0) {
          updateTutor(index, 'nombreTutor', 'NO ENCONTRADO');
          updateTutor(index, 'tutorId', '');
        } else if (results.length === 1) {
          const s = results[0];
          const nombre = s.personaFisica ? `${s.personaFisica.nombres} ${s.personaFisica.apellidos}` : (s.personaJuridica?.razonSocial || 'Socio Encontrado');
          updateTutor(index, 'nombreTutor', nombre);
          updateTutor(index, 'tutorId', s.id); // Guardamos el ID real
        } else {
          updateTutor(index, 'nombreTutor', 'MÚLTIPLES COINCIDENCIAS - SEA MÁS ESPECÍFICO');
          updateTutor(index, 'tutorId', '');
        }
      } else {
        updateTutor(index, 'nombreTutor', 'ERROR EN SERVIDOR');
      }
    } catch (err) {
      updateTutor(index, 'nombreTutor', 'ERROR DE CONEXIÓN');
    }
  };

  const handleFileChange = (e, setFile, setPreview) => {
    const file = e.target.files[0];
    if (file && ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      setFile(file);
      setPreview(URL.createObjectURL(file));
    } else {
      alert('Solo imágenes JPG/PNG.');
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    // Modificamos la validación: Solo exigir si no tenemos ni el archivo nuevo ni la previsualización previa
    if ((!cedulaFrontal && !previewFrontal) || (!cedulaDorso && !previewDorso)) {
      Swal.fire('Documentos', 'Debe adjuntar Cédula (Frente y Dorso)', 'warning');
      return;
    }

    const isFisica = formData.tipoPersona.valor === 'FISICA';
    const payload = {
      tipoPersonaId: formData.tipoPersona.id,
      // Fisica
      nombre: isFisica ? formData.nombres : null,
      apellido: isFisica ? formData.apellidos : null,
      numeroDocumento: isFisica ? formData.numeroDocumento : null,
      fechaNacimiento: isFisica ? formData.fechaNacimiento : null,
      tipoDocumentoId: isFisica ? (formData.tipoDocumento.id || null) : null,
      generoId: isFisica ? (formData.genero.id || null) : null,
      estadoCivilId: isFisica ? (formData.estadoCivil.id || null) : null,
      // Conyuge
      conyugeNombre: formData.conyugeNombre,
      conyugeDocumento: formData.conyugeDocumento,
      // Juridica
      razonSocial: !isFisica ? formData.razonSocial : null,
      ruc: !isFisica ? formData.ruc : null,
      fechaConstitucion: !isFisica ? formData.fechaConstitucion : null,
      representanteLegal: !isFisica ? formData.representanteLegal : null,
      // Common
      nacionalidadId: formData.nacionalidad.id || null,
      lugarNacimiento: formData.lugarNacimiento,

      tipoSocioId: formData.tipoSocio.id || null, // Updated
      calificacionRiesgoId: formData.calificacionRiesgo.id || null, 
      nivelFidelidadId: formData.nivelFidelidad.id || null, 
      categoriaId: formData.categoria.id || null, 

      // New Nested Data
      esPep: formData.esPep,
      datosLaborales: formData.datosLaborales,
      referencias: formData.referencias,
      beneficiarios: formData.beneficiarios.filter(b => b.nombreCompleto && b.numeroDocumento),

      contactos: [
        { tipoContacto: 'CELULAR', valor: `${formData.prefijoPrincipal} ${formData.celularPrincipal}`.trim(), esPrincipal: true },
        ...(formData.celularSecundario ? [{ tipoContacto: 'CELULAR', valor: `${formData.prefijoSecundario} ${formData.celularSecundario}`.trim(), esPrincipal: false }] : []),
        ...(formData.emailContacto ? [{ tipoContacto: 'EMAIL', valor: formData.emailContacto, esPrincipal: false }] : [])
      ],
      perfilEconomico: formData.perfilEconomico,
      tutores: formData.tutores.map(t => ({
        id: t.id || null,
        tutorId: t.tutorId,
        parentescoId: t.parentescoId,
        esPrincipal: t.esPrincipal
      })),
      servicios: formData.servicios,

      // Address Fields
      departamentoId: formData.departamentoId,
      ciudadId: formData.ciudadId,
      barrioId: formData.barrioId,
      tipoDireccionId: formData.tipoDireccionId,
      nroCasa: formData.nroCasa,
      direccionExacta: formData.direccionExacta,
      referencia: formData.referencia
    };

    const data = new FormData();
    data.append('socio', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
    if (cedulaFrontal) data.append('cedulaFrontal', cedulaFrontal);
    if (cedulaDorso) data.append('cedulaDorso', cedulaDorso);

    try {
      Swal.fire({ title: 'Procesando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      const resp = await fetch(`${API_BASE_URL}/socio/${id}/completo`, {
        method: 'PUT',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: data
      });
      if (!resp.ok) throw new Error(await resp.text());
      const newSocio = await resp.json();

      setCreatedSocioId(newSocio.id);

      const pdfResp = await fetch(`${API_BASE_URL}/socio/contrato/${newSocio.id}/descargar`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (pdfResp.ok) {
        const blob = await pdfResp.blob();
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        setShowPreview(true);
        Swal.close();
      } else {
        Swal.fire('Advertencia', 'Socio creado, pero error al cargar la vista previa del contrato.', 'warning');
        navigate(`/socios/${newSocio.id}/verificar-solicitud`);
      }


    } catch (error) {
      if (error.message && error.message.includes('ya se encuentra registrado')) {
        Swal.fire('Duplicado', 'Ya existe un socio con este documento.', 'error');
        return;
      }
      Swal.fire('Error', `No se pudo registrar: ${error.message}`, 'error');
    }
  };

  const handleConfirmSignature = async () => {
    try {
      Swal.fire({ title: 'Confirmando Firma...', text: 'Generando obligaciones iniciales...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

      const resp = await fetch(`${API_BASE_URL}/socio/${createdSocioId}/confirmar`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (!resp.ok) throw new Error(await resp.text());

      Swal.fire({
        icon: 'success',
        title: 'Solicitud Finalizada',
        text: 'Registro completado. Pase a Caja para aportes.',
        confirmButtonText: 'Ir a Inicio'
      }).then(() => {
        navigate('/');
      });
    } catch (error) {
      Swal.fire('Error', error.message, 'error');
    }
  };

  const handlePrint = () => {
    const iframe = document.getElementById('pdf-frame');
    if (iframe) iframe.contentWindow.print();
  };

  const renderPreviewModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden">
        <div className="bg-green-700 text-white p-4 flex justify-between items-center"><h3 className="font-bold">Contrato</h3><button onClick={() => setShowPreview(false)}>X</button></div>
        <div className="flex-1 bg-gray-100 p-4 relative">{pdfUrl && <iframe id="pdf-frame" src={pdfUrl} className="w-full h-full bg-white" title="PDF"></iframe>}</div>
        <div className="p-4 bg-white flex justify-between"><button onClick={handlePrint} className="px-4 py-2 bg-blue-600 text-white rounded">Imprimir</button><button onClick={handleConfirmSignature} className="px-4 py-2 bg-green-600 text-white rounded">Confirmar</button></div>
      </div>
    </div>
  );

  // --- RENDER HELPERS ---




  // --- RENDER STEPS ---
  const renderStep1 = () => (
    <div className="space-y-12 animate-premium-in mt-6">
      {/* SECCIÓN CLASIFICACIÓN (READ-ONLY) */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
        <div className="md:col-span-12 p-8 bg-slate-900 rounded-[2.5rem] shadow-2xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-64 h-64 bg-uni-teal/10 rounded-full blur-3xl -mr-32 -mt-32 group-hover:bg-uni-teal/20 transition-colors duration-700"></div>
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="space-y-3">
              <p className="text-[10px] font-black text-uni-teal uppercase tracking-[0.3em]">Estado de Membresía</p>
              <div className="flex items-center gap-3">
                <select name="tipoSocio" value={formData.tipoSocio.id} onChange={handleInputChange} className="bg-transparent border-none text-white text-2xl font-black focus:ring-0 p-0 w-full appearance-none">
                  <option value="" className="bg-slate-900">Por Defecto</option>
                  {tiposSocio?.map(t => <option key={t.id} value={t.id} className="bg-slate-900">{t.descripcion}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-3 border-l border-white/10 pl-10">
              <p className="text-[10px] font-black text-uni-teal uppercase tracking-[0.3em]">Categoría de Socio (Riesgo)</p>
              <div className="flex items-center gap-3">
                <select 
                  name="categoria" 
                  value={formData.categoria.id} 
                  onChange={handleInputChange} 
                  className="bg-transparent border-none text-white text-2xl font-black focus:ring-0 p-0 w-full appearance-none"
                >
                  <option value="" className="bg-slate-900 text-slate-400">Sin Categoría</option>
                  {categorias?.map(c => <option key={c.id} value={c.id} className="bg-slate-900">{c.nombreCategoria}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-3 border-l border-white/10 pl-10">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">Riesgo Interno (Histórico)</p>
              <p className="text-white text-2xl font-black flex items-center gap-3">
                <ShieldCheck className="w-6 h-6 text-uni-teal" /> 
                {calificacionesRiesgo.find(c => c.id === formData.calificacionRiesgo.id)?.descripcion || 'A - Riesgo Bajo'}
              </p>
            </div>
            <div className="space-y-3 border-l border-white/10 pl-10">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">Fidelidad (Loyalty)</p>
              <p className="text-white text-2xl font-black flex items-center gap-3 text-uni-teal">
                <RotateCcw className="w-6 h-6" /> {nivelesFidelidad.find(n => n.id === formData.nivelFidelidad.id)?.descripcion || 'STD'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-x-10 gap-y-12">
        {formData.tipoPersona.valor === 'FISICA' ? (
          <>
            <div className="md:col-span-4"><Field label="Nombres"><input type="text" name="nombres" value={formData.nombres} onChange={handleInputChange} required className={inputClass} /></Field></div>
            <div className="md:col-span-4"><Field label="Apellidos"><input type="text" name="apellidos" value={formData.apellidos} onChange={handleInputChange} required className={inputClass} /></Field></div>
            <div className="md:col-span-4">
              <Field label="Tipo Documento">
                <select name="tipoDocumento" value={formData.tipoDocumento.id} onChange={handleInputChange} className={inputClass}>
                  <option value="">Seleccione...</option>
                  {tiposDocumento?.map(t => <option key={t.id} value={t.id}>{t.nombreTipo}</option>)}
                </select>
              </Field>
            </div>
            <div className="md:col-span-4">
              <Field label="Nro Documento">
                <input
                  type="text"
                  name="numeroDocumento"
                  value={formData.numeroDocumento}
                  onChange={handleInputChange}
                  required
                  className={inputClass}
                  disabled={!isAdmin && isEditingMode}
                />
              </Field>
            </div>
            <div className="md:col-span-4"><Field label="Fecha Nacimiento"><input type="date" name="fechaNacimiento" value={formData.fechaNacimiento} onChange={handleInputChange} className={inputClass} /></Field></div>
            <div className="md:col-span-4">
              <Field label="Género">
                <select name="genero" value={formData.genero.id} onChange={handleInputChange} className={inputClass}>
                  <option value="">Seleccione...</option>
                  {generos?.map(g => <option key={g.id} value={g.id}>{g.descripcion}</option>)}
                </select>
              </Field>
            </div>
            <div className="md:col-span-4">
              <Field label="Estado Civil">
                <select name="estadoCivil" value={formData.estadoCivil.id} onChange={handleInputChange} className={inputClass}>
                  <option value="">Seleccione...</option>
                  {estadosCiviles?.map(e => <option key={e.id} value={e.id}>{e.descripcion}</option>)}
                </select>
              </Field>
            </div>
            <div className="md:col-span-4">
              <Field label="Nacionalidad">
                <select name="nacionalidad" value={formData.nacionalidad.id} onChange={handleInputChange} required className={inputClass}>
                  <option value="">Seleccione...</option>
                  {nacionalidades?.map(n => <option key={n.id} value={n.id}>{n.pais}</option>)}
                </select>
              </Field>
            </div>
            <div className="md:col-span-4">
              <Field label="Lugar de Nacimiento">
                <input
                  type="text"
                  name="lugarNacimiento"
                  value={formData.lugarNacimiento}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </Field>
            </div>

            {/* SECCIÓN TUTORES PARA MENORES O JUVENILES */}
            {(formData.fechaNacimiento && Math.floor((new Date() - new Date(formData.fechaNacimiento)) / (365.25 * 24 * 60 * 60 * 1000)) < 18 ||
              tiposSocio.find(t => t.id === formData.tipoSocio.id)?.descripcion?.toUpperCase().includes('JUVENIL')) && (
                <div className="col-span-full p-10 bg-blue-50/50 rounded-[2.5rem] border border-blue-100 shadow-inner mt-8">
                  <div className="flex justify-between items-center mb-8">
                    <h4 className="text-[10px] font-black text-blue-600 uppercase tracking-widest flex items-center gap-3">
                      <Users className="w-4 h-4" /> Vinculación de Tutores Legales (Socio Menor)
                    </h4>
                    <button type="button" onClick={addTutor} className="px-4 py-2 bg-blue-600 text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-blue-700 transition-all flex items-center gap-2">
                      <Plus className="w-3 h-3" /> Agregar Tutor
                    </button>
                  </div>

                  <div className="space-y-4">
                    {formData.tutores.map((t, idx) => (
                      <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-6 p-6 bg-white rounded-2xl border border-blue-100 items-end">
                        <div className="md:col-span-3">
                          <Field label="Buscar Tutor">
                            <input
                              type="text"
                              value={t.terminoBusqueda || t.tutorId}
                              onChange={(e) => updateTutor(idx, 'terminoBusqueda', e.target.value)}
                              onBlur={(e) => buscarTutor(idx, e.target.value)}
                              className={inputClass}
                              placeholder="CI, Nombre o Nro Socio"
                            />
                          </Field>
                        </div>
                        <div className="md:col-span-4">
                          <div className="flex flex-col">
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Nombre del Tutor</span>
                            <div className={`p-4 rounded-xl text-xs font-black uppercase tracking-tight ${t.nombreTutor === 'NO ENCONTRADO' ? 'bg-rose-50 text-rose-600' : 'bg-slate-50 text-slate-600'}`}>
                              {t.nombreTutor || 'Ingrese ID y tabule para buscar...'}
                            </div>
                          </div>
                        </div>
                        <div className="md:col-span-2">
                          <Field label="Parentesco">
                            <select
                              value={t.parentescoId}
                              onChange={(e) => updateTutor(idx, 'parentescoId', e.target.value)}
                              className={`${inputClass} border-blue-200 focus:border-blue-500 text-slate-900 font-bold`}
                            >
                              <option value="">Seleccione...</option>
                              {parentescos?.map(p => (
                                <option key={p.id} value={p.id} className="text-slate-900">
                                  {p.nombre}
                                </option>
                              ))}
                            </select>
                          </Field>
                        </div>
                        <div className="md:col-span-2 flex items-center gap-2 mb-4">
                          <input
                            type="checkbox"
                            checked={t.esPrincipal}
                            onChange={(e) => updateTutor(idx, 'esPrincipal', e.target.checked)}
                            className="w-4 h-4 text-blue-600"
                          />
                          <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Principal</span>
                        </div>
                        <div className="md:col-span-1 flex justify-end">
                          <button type="button" onClick={() => removeTutor(idx)} className="p-3 text-rose-500 hover:bg-rose-50 rounded-xl transition-all">
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                    {formData.tutores.length === 0 && (
                      <div className="text-center py-8 bg-white/50 rounded-2xl border-2 border-dashed border-blue-100">
                        <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest">No hay tutores vinculados. Haga clic en "Agregar Tutor".</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

            {estadosCiviles?.find(e => e.id === formData.estadoCivil.id)?.descripcion?.toUpperCase().includes('CASADO') && (
              <div className="col-span-full grid grid-cols-1 md:grid-cols-12 gap-8 p-10 bg-slate-50 rounded-[2.5rem] border border-slate-100 shadow-inner mt-8">
                <h4 className="md:col-span-12 text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-3">
                  <User className="w-4 h-4" /> Datos de Identidad del Cónyuge
                </h4>
                <div className="md:col-span-8"><Field label="Nombre Completo Cónyuge"><input type="text" name="conyugeNombre" value={formData.conyugeNombre} onChange={handleInputChange} className={inputClass} placeholder=" " /></Field></div>
                <div className="md:col-span-4"><Field label="Cédula Cónyuge"><input type="text" name="conyugeDocumento" value={formData.conyugeDocumento} onChange={handleInputChange} className={inputClass} placeholder=" " /></Field></div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="md:col-span-8"><Field label="Razón Social"><input type="text" name="razonSocial" value={formData.razonSocial} onChange={handleInputChange} required className={inputClass} placeholder=" " /></Field></div>
            <div className="md:col-span-4">
              <Field label="RUC">
                <input
                  type="text"
                  name="ruc"
                  value={formData.ruc}
                  onChange={handleInputChange}
                  required
                  className={inputClass}
                  disabled={!isAdmin && isEditingMode}
                />
              </Field>
            </div>
            <div className="md:col-span-4"><Field label="Fecha Constitución"><input type="date" name="fechaConstitucion" value={formData.fechaConstitucion} onChange={handleInputChange} className={inputClass} /></Field></div>
            <div className="md:col-span-8"><Field label="Representante Legal"><input type="text" name="representanteLegal" value={formData.representanteLegal} onChange={handleInputChange} className={inputClass} placeholder=" " /></Field></div>
            <div className="md:col-span-4">
              <Field label="Nacionalidad">
                <select name="nacionalidad" value={formData.nacionalidad.id} onChange={handleInputChange} required className={inputClass}>
                  <option value="">Seleccione...</option>
                  {nacionalidades?.map(n => <option key={n.id} value={n.id}>{n.pais}</option>)}
                </select>
              </Field>
            </div>
          </>
        )}



        {/* ADDRESS SECTION */}
        <div className="col-span-full p-12 bg-slate-50/50 rounded-[3rem] border border-slate-200 shadow-sm mt-8">
          <div className="flex items-center gap-4 mb-10">
            <div className="p-3 bg-white rounded-2xl shadow-sm border border-slate-200 text-uni-teal">
              <MapPin className="w-6 h-6" />
            </div>
            <div>
              <h4 className="text-[12px] font-black uppercase tracking-[0.2em] text-slate-900">Ubicación y Georreferencia</h4>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Precisión en la ubicación para servicios y notificaciones</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-x-10 gap-y-12">
            <div className="md:col-span-4">
              <Field label="Departamento">
                <select name="departamentoId" value={formData.departamentoId} onChange={e => {
                  const val = e.target.value ? Number(e.target.value) : '';
                  setFormData(prev => ({ ...prev, departamentoId: val, ciudadId: '', barrioId: '' }));
                }} className={inputClass}>
                  <option value="">Seleccione...</option>
                  {departamentos?.map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}
                </select>
              </Field>
            </div>
            <div className="md:col-span-4">
              <Field label="Ciudad">
                <select name="ciudadId" value={formData.ciudadId} onChange={e => {
                  const val = e.target.value ? Number(e.target.value) : '';
                  setFormData(prev => ({ ...prev, ciudadId: val, barrioId: '' }));
                }} className={inputClass} disabled={!formData.departamentoId}>
                  <option value="">Seleccione...</option>
                  {ciudades?.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </Field>
            </div>
            <div className="md:col-span-4">
              <Field label="Barrio">
                <select name="barrioId" value={formData.barrioId} onChange={e => {
                  const val = e.target.value ? Number(e.target.value) : '';
                  setFormData(prev => ({ ...prev, barrioId: val }));
                }} className={inputClass} disabled={!formData.ciudadId}>
                  <option value="">Seleccione...</option>
                  {barrios?.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                </select>
              </Field>
            </div>
            <div className="md:col-span-3">
              <Field label="Tipo Dirección">
                <select name="tipoDireccionId" value={formData.tipoDireccionId} onChange={handleInputChange} className={inputClass}>
                  <option value="">Seleccione...</option>
                  {tiposDireccion?.map(t => <option key={t.id} value={t.id}>{t.descripcion}</option>)}
                </select>
              </Field>
            </div>
            <div className="md:col-span-6">
              <Field label="Dirección Exacta">
                <input type="text" name="direccionExacta" value={formData.direccionExacta} onChange={handleInputChange} className={inputClass} placeholder=" " />
              </Field>
            </div>
            <div className="md:col-span-3">
              <Field label="Nro. Casa">
                <input type="text" name="nroCasa" value={formData.nroCasa} onChange={handleInputChange} className={inputClass} placeholder=" " />
              </Field>
            </div>
            <div className="md:col-span-12">
              <Field label="Referencia">
                <input type="text" name="referencia" value={formData.referencia} onChange={handleInputChange} className={inputClass} placeholder=" " />
              </Field>
            </div>
          </div>
        </div>

        <div className="col-span-full">
          <SocioServiciosSection
            servicios={formData.servicios || []}
            onChange={(updated) => setFormData({ ...formData, servicios: updated })}
          />
        </div>
      </div>

      {/* CONTACT SECTION */}
      <div className="col-span-full p-12 bg-white rounded-[3rem] border border-slate-200 shadow-2xl shadow-slate-900/5 mt-8">
        <div className="flex items-center gap-4 mb-10">
          <div className="p-3 bg-slate-50 rounded-2xl shadow-sm border border-slate-100 text-slate-400">
            <Phone className="w-6 h-6" />
          </div>
          <div>
            <h4 className="text-[12px] font-black uppercase tracking-[0.2em] text-slate-900">Canales de Comunicación</h4>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Gestione sus medios de contacto para notificaciones críticas</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-10 gap-y-12">
          <div className="md:col-span-4">
            <Field label="Celular Principal">
              <div className="flex items-center gap-4">
                <select
                  className="bg-slate-50 border-none rounded-xl text-xs font-black uppercase tracking-widest px-4 py-4 w-28 appearance-none"
                  onChange={(e) => setFormData(prev => ({ ...prev, prefijoPrincipal: e.target.value }))}
                  value={formData.prefijoPrincipal}
                >
                  <option value="+595">🇵🇾 +595</option>
                  <option value="+54">🇦🇷 +54</option>
                  <option value="+55">🇧🇷 +55</option>
                  <option value="+591">🇧🇴 +591</option>
                </select>
                <input
                  type="text"
                  name="celularPrincipal"
                  value={formData.celularPrincipal}
                  onChange={handleInputChange}
                  required
                  className={inputClass + " flex-1"}
                  placeholder=" "
                />
              </div>
            </Field>
          </div>
          <div className="md:col-span-4">
            <Field label="Celular Secundario">
              <div className="flex items-center gap-4">
                <select
                  className="bg-slate-50 border-none rounded-xl text-xs font-black uppercase tracking-widest px-4 py-4 w-28 appearance-none"
                  onChange={(e) => setFormData(prev => ({ ...prev, prefijoSecundario: e.target.value }))}
                  value={formData.prefijoSecundario}
                >
                  <option value="+595">🇵🇾 +595</option>
                  <option value="+54">🇦🇷 +54</option>
                  <option value="+55">🇧🇷 +55</option>
                  <option value="+591">🇧🇴 +591</option>
                </select>
                <input
                  type="text"
                  name="celularSecundario"
                  value={formData.celularSecundario}
                  onChange={handleInputChange}
                  className={inputClass + " flex-1"}
                  placeholder=" "
                />
              </div>
            </Field>
          </div>
          <div className="md:col-span-4">
            <Field label="Correo Electrónico">
              <input type="email" name="emailContacto" value={formData.emailContacto} onChange={handleInputChange} className={inputClass} placeholder=" " />
            </Field>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => {
    return (
      <div className="space-y-12 animate-premium-in mt-6">
        <div className="col-span-full space-y-8">
          <div className="flex justify-between items-center bg-slate-50 p-8 rounded-[2rem] border border-slate-100 shadow-inner">
            <div>
              <h4 className="text-[12px] font-black text-uni-teal uppercase tracking-[0.25em] flex items-center gap-3">
                <DollarSign className="w-5 h-5" /> Perfil Laboral y Declaración de Ingresos
              </h4>
              <p className="text-[10px] text-slate-400 font-bold uppercase mt-2 tracking-widest">
                Registre sus actividades económicas de forma independiente para mejorar su calificación
              </p>
            </div>
            <button
              type="button"
              onClick={addPerfil}
              className="flex items-center gap-2 px-8 py-4 bg-white border border-slate-200 rounded-2xl text-[10px] font-black text-slate-600 uppercase tracking-[0.15em] hover:bg-slate-900 hover:text-white transition-all shadow-xl hover:scale-105 active:scale-95"
            >
              <Plus className="w-4 h-4" /> Agregar Actividad
            </button>
          </div>

          <div className="space-y-12">
            {formData.perfilEconomico.map((pe, idx) => (
              <div key={idx} className="relative group p-12 bg-white rounded-[3rem] border border-slate-200 shadow-2xl shadow-slate-900/5 animate-premium-in hover:border-uni-teal/30 transition-colors">
                {idx >= 2 && (
                  <button
                    type="button"
                    onClick={() => removePerfil(idx)}
                    className="absolute -top-4 -right-4 w-12 h-12 bg-rose-500 text-white rounded-2xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-2xl z-20 hover:scale-110 active:scale-90"
                  >
                    <Trash2 className="w-6 h-6" />
                  </button>
                )}
                <div className="absolute -top-5 left-10 bg-slate-900 text-white px-8 py-3 rounded-2xl text-[11px] font-black uppercase tracking-[0.2em] shadow-2xl ring-4 ring-white">
                  Ingreso #{idx + 1}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-x-10 gap-y-12 mt-6">
                  <div className="md:col-span-4">
                    <Field label="Origen de Fondos / Modalidad">
                      <select value={pe.tipoIngresoId} onChange={e => updatePerfil(idx, 'tipoIngresoId', e.target.value)} className={inputClass}>
                        <option value="">Seleccione...</option>
                        {tiposIngreso.map(ti => <option key={ti.id} value={ti.id}>{ti.nombre}</option>)}
                      </select>
                    </Field>
                  </div>
                  <div className="md:col-span-4">
                    <Field label="Monto Mensual Promedio (Gs.)">
                      <input
                        type="text"
                        value={pe.montoMensualPromedio ? new Intl.NumberFormat('es-PY').format(pe.montoMensualPromedio) : ''}
                        onChange={(e) => updatePerfil(idx, 'montoMensualPromedio', e.target.value.replace(/\D/g, ''))}
                        className={inputClass + " font-black text-uni-teal text-lg"}
                        placeholder=" "
                      />
                    </Field>
                  </div>
                  <div className="md:col-span-4">
                    <Field label="Profesión / Cargo">
                      <select value={pe.profesionId} onChange={(e) => updatePerfil(idx, 'profesionId', e.target.value)} className={inputClass}>
                        <option value="">Seleccione...</option>
                        {profesiones?.map(prof => <option key={prof.id} value={prof.id}>{prof.nombre}</option>)}
                      </select>
                    </Field>
                  </div>
                  <div className="md:col-span-6">
                    <Field label="Empresa / Institución">
                      <input type="text" value={pe.empresa || ''} onChange={(e) => updatePerfil(idx, 'empresa', e.target.value)} className={inputClass} placeholder=" " />
                    </Field>
                  </div>
                  <div className="md:col-span-6">
                    <Field label="Dirección Laboral">
                      <input type="text" value={pe.direccion || ''} onChange={(e) => updatePerfil(idx, 'direccion', e.target.value)} className={inputClass} placeholder=" " />
                    </Field>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderStep3 = () => (
    <div className="space-y-12 animate-premium-in mt-6">
      <div className="col-span-full space-y-8">
        <div className="flex justify-between items-center bg-slate-50 p-8 rounded-[2rem] border border-slate-100 shadow-inner">
          <div>
            <h4 className="text-[12px] font-black text-uni-teal uppercase tracking-[0.25em] flex items-center gap-3">
              <Users className="w-5 h-5" /> Referencias Personales y Comerciales
            </h4>
            <p className="text-[10px] text-slate-400 font-bold uppercase mt-2 tracking-widest">
              Añada contactos que puedan validar su trayectoria y solvencia
            </p>
          </div>
          <button
            type="button"
            onClick={addReferencia}
            className="flex items-center gap-2 px-8 py-4 bg-white border border-slate-200 rounded-2xl text-[10px] font-black text-slate-600 uppercase tracking-[0.15em] hover:bg-slate-900 hover:text-white transition-all shadow-xl hover:scale-105 active:scale-95"
          >
            <Plus className="w-4 h-4" /> Agregar Referencia
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {formData.referencias?.map((ref, idx) => (
            <div key={idx} className="relative group p-10 bg-white rounded-[2.5rem] border border-slate-200 shadow-2xl shadow-slate-900/5 animate-premium-in hover:border-uni-teal/30 transition-colors">
              <button
                type="button"
                onClick={() => removeReferencia(idx)}
                className="absolute -top-4 -right-4 w-12 h-12 bg-rose-500 text-white rounded-2xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-2xl z-20 hover:scale-110 active:scale-90"
              >
                <Trash2 className="w-6 h-6" />
              </button>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-8">
                <div className="md:col-span-12">
                  <Field label="Nombre Completo"><input type="text" value={ref.nombre} onChange={(e) => updateReferencia(idx, 'nombre', e.target.value)} className={inputClass} placeholder=" " /></Field>
                </div>
                <div className="md:col-span-6">
                  <Field label="Teléfono"><input type="text" value={ref.telefono} onChange={(e) => updateReferencia(idx, 'telefono', e.target.value)} className={inputClass} placeholder=" " /></Field>
                </div>
                <div className="md:col-span-6">
                  <Field label="Tipo de Referencia">
                    <select value={ref.tipoReferencia} onChange={(e) => updateReferencia(idx, 'tipoReferencia', e.target.value)} className={inputClass}>
                      <option value="PERSONAL">Personal</option>
                      <option value="COMERCIAL">Comercial</option>
                    </select>
                  </Field>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-12 animate-premium-in mt-6">
      <div className="col-span-full space-y-8">
        <div className="flex justify-between items-center bg-slate-50 p-8 rounded-[2rem] border border-slate-100 shadow-inner">
          <div>
            <h4 className="text-[12px] font-black text-uni-teal uppercase tracking-[0.25em] flex items-center gap-3">
              <Heart className="w-5 h-5" /> Beneficiarios Designados
            </h4>
            <p className="text-[10px] text-slate-400 font-bold uppercase mt-2 tracking-widest">
              Especifique quiénes recibirán los beneficios de su membresía
            </p>
          </div>
          <button
            type="button"
            onClick={addBeneficiario}
            className="flex items-center gap-2 px-8 py-4 bg-white border border-slate-200 rounded-2xl text-[10px] font-black text-slate-600 uppercase tracking-[0.15em] hover:bg-slate-900 hover:text-white transition-all shadow-xl hover:scale-105 active:scale-95"
          >
            <Plus className="w-4 h-4" /> Agregar Beneficiario
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {formData.beneficiarios?.map((ben, idx) => (
            <div key={idx} className="relative group p-10 bg-white rounded-[2.5rem] border border-slate-200 shadow-2xl shadow-slate-900/5 animate-premium-in hover:border-uni-teal/30 transition-colors">
              <button
                type="button"
                onClick={() => removeBeneficiario(idx)}
                className="absolute -top-4 -right-4 w-12 h-12 bg-rose-500 text-white rounded-2xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-2xl z-20 hover:scale-110 active:scale-90"
              >
                <Trash2 className="w-6 h-6" />
              </button>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-8">
                <div className="md:col-span-12">
                  <Field label="Nombre Completo"><input type="text" value={ben.nombreCompleto} onChange={(e) => updateBeneficiario(idx, 'nombreCompleto', e.target.value)} className={inputClass} /></Field>
                </div>
                <div className="md:col-span-12">
                  <Field label="Número de Documento"><input type="text" value={ben.numeroDocumento} onChange={(e) => updateBeneficiario(idx, 'numeroDocumento', e.target.value)} className={inputClass} /></Field>
                </div>
                <div className="md:col-span-12">
                  <Field label="Parentesco">
                    <select value={ben.parentescoId} onChange={(e) => updateBeneficiario(idx, 'parentescoId', e.target.value)} className={inputClass}>
                      <option value="">Seleccione...</option>
                      {parentescos?.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                    </select>
                  </Field>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-12 animate-premium-in mt-6">
      <div className="col-span-full space-y-8">
        <div className="bg-slate-50 p-8 rounded-[2rem] border border-slate-100 shadow-inner">
          <h4 className="text-[12px] font-black text-uni-teal uppercase tracking-[0.25em] flex items-center gap-3">
            <FileText className="w-5 h-5" /> Expediente Digital y Documentación
          </h4>
          <p className="text-[10px] text-slate-400 font-bold uppercase mt-2 tracking-widest">
            Adjunte imágenes claras de los documentos requeridos
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="p-10 bg-white rounded-[3rem] border border-slate-200 shadow-2xl shadow-slate-900/5 text-center">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Frente del Documento</p>
            {previewFrontal ? (
              <div className="space-y-6">
                <div className="relative group inline-block">
                  <img
                    src={previewFrontal}
                    alt="Frente"
                    className="h-48 mx-auto object-contain rounded-2xl shadow-2xl border cursor-pointer hover:scale-105 transition-transform"
                    onClick={() => Swal.fire({ imageUrl: previewFrontal, imageAlt: 'Frente', width: '80%', showConfirmButton: false, showCloseButton: true })}
                  />
                </div>
                {isEditingMode && (
                  <button type="button" onClick={() => { setPreviewFrontal(null); setCedulaFrontal(null); }} className="w-full py-4 bg-rose-50 text-rose-600 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-rose-100 transition-colors">
                    Reemplazar Documento
                  </button>
                )}
              </div>
            ) : (
              <div className="relative h-48 border-4 border-dashed border-slate-100 rounded-[2rem] flex flex-col items-center justify-center bg-slate-50/50 hover:bg-slate-50 transition-colors cursor-pointer group">
                <Upload className="w-10 h-10 text-slate-200 group-hover:text-uni-teal transition-colors mb-4" />
                <input type="file" onChange={(e) => handleFileChange(e, setCedulaFrontal, setPreviewFrontal)} disabled={!isEditingMode} className="absolute inset-0 opacity-0 cursor-pointer" />
                <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Subir Archivo</p>
              </div>
            )}
          </div>

          <div className="p-10 bg-white rounded-[3rem] border border-slate-200 shadow-2xl shadow-slate-900/5 text-center">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Dorso del Documento</p>
            {previewDorso ? (
              <div className="space-y-6">
                <div className="relative group inline-block">
                  <img
                    src={previewDorso}
                    alt="Dorso"
                    className="h-48 mx-auto object-contain rounded-2xl shadow-2xl border cursor-pointer hover:scale-105 transition-transform"
                    onClick={() => Swal.fire({ imageUrl: previewDorso, imageAlt: 'Dorso', width: '80%', showConfirmButton: false, showCloseButton: true })}
                  />
                </div>
                {isEditingMode && (
                  <button type="button" onClick={() => { setPreviewDorso(null); setCedulaDorso(null); }} className="w-full py-4 bg-rose-50 text-rose-600 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-rose-100 transition-colors">
                    Reemplazar Documento
                  </button>
                )}
              </div>
            ) : (
              <div className="relative h-48 border-4 border-dashed border-slate-100 rounded-[2rem] flex flex-col items-center justify-center bg-slate-50/50 hover:bg-slate-50 transition-colors cursor-pointer group">
                <Upload className="w-10 h-10 text-slate-200 group-hover:text-uni-teal transition-colors mb-4" />
                <input type="file" onChange={(e) => handleFileChange(e, setCedulaDorso, setPreviewDorso)} disabled={!isEditingMode} className="absolute inset-0 opacity-0 cursor-pointer" />
                <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Subir Archivo</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const handleBaja = async () => {
    const result = await Swal.fire({
      title: '¿Dar de baja?',
      text: "El socio pasará a estado INACTIVO.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, dar de baja'
    });

    if (result.isConfirmed) {
      try {
        const res = await fetch(`${API_BASE_URL}/socio/${id}`, {
          method: 'DELETE',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (res.ok) {
          Swal.fire('Inactivo', 'El socio ha sido dado de baja.', 'success');
          fetchSocioOriginal();
        } else {
          throw new Error('Error al dar de baja');
        }
      } catch (err) {
        Swal.fire('Error', err.message, 'error');
      }
    }
  };

  const handleReactivar = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/socio/${id}/reactivar`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        Swal.fire('Reactivo', 'El socio ha sido reactivado.', 'success');
        fetchSocioOriginal();
      } else {
        throw new Error('Error al reactivar');
      }
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    }
  };

  if (loading) return <div className="p-8 text-center bg-gray-50 min-h-screen">Cargando...</div>;
  if (!socioData) return <div className="p-8 text-center bg-gray-50 min-h-screen">No se encontró el socio.</div>;

  return (
    <div className="p-8 bg-slate-50 min-h-screen font-sans selection:bg-uni-teal selection:text-white">
      <div className="max-w-[1400px] mx-auto space-y-12">

        {/* ENCABEZADO PREMIUM */}
        <div className="bg-slate-900 rounded-[3rem] p-10 shadow-2xl relative overflow-hidden flex flex-col md:flex-row justify-between items-start md:items-center gap-8 group">
          {/* Background Accent */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-uni-teal/20 to-transparent rounded-full blur-3xl -mr-48 -mt-48 transition-all duration-1000 group-hover:scale-110"></div>

          <div className="relative z-10 flex items-center gap-6">
            <button
              onClick={() => navigate('/socios')}
              className="w-14 h-14 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center text-white hover:bg-white hover:text-slate-900 transition-all shadow-xl hover:-translate-x-1"
            >
              <ArrowLeft size={24} />
            </button>
            <div>
              <h1 className="text-3xl font-black text-white tracking-tight flex items-center gap-4">
                Expediente de Socio <span className="text-uni-teal bg-uni-teal/10 px-4 py-1 rounded-xl">#{socioData.numeroSocio}</span>
              </h1>
              <p className="text-slate-400 font-bold uppercase tracking-[0.2em] text-[10px] mt-2 ml-1">
                Centro de Gestión y Auditoría de Membresía
              </p>
            </div>
          </div>

          <div className="relative z-10 flex flex-wrap items-center gap-4 bg-white/5 p-3 rounded-[2rem] border border-white/10 backdrop-blur-xl">
            <label className="flex items-center gap-3 cursor-pointer px-6 py-3 rounded-2xl hover:bg-white/5 transition-colors">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={isEditingMode}
                  onChange={e => setIsEditingMode(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-12 h-6 bg-slate-700 rounded-full peer peer-checked:bg-uni-teal transition-colors"></div>
                <div className="absolute top-1 left-1 w-4 h-4 bg-white rounded-full peer-checked:translate-x-6 transition-transform"></div>
              </div>
              <span className="text-[10px] font-black text-white uppercase tracking-widest">Modo Edición</span>
            </label>

            <div className="h-8 w-px bg-white/10 mx-2 hidden md:block"></div>

            {isActivo ? (
              <button onClick={handleBaja} className="flex items-center gap-3 px-8 py-3 bg-rose-500/10 text-rose-500 border border-rose-500/20 rounded-2xl hover:bg-rose-500 hover:text-white transition-all text-[10px] font-black uppercase tracking-widest shadow-xl">
                <AlertTriangle size={18} /> Dar de Baja
              </button>
            ) : (
              <button onClick={handleReactivar} className="flex items-center gap-3 px-8 py-3 bg-uni-teal/10 text-uni-teal border border-uni-teal/20 rounded-2xl hover:bg-uni-teal hover:text-white transition-all text-[10px] font-black uppercase tracking-widest shadow-xl">
                <RotateCcw size={18} /> Reactivar Membresía
              </button>
            )}
          </div>
        </div>

        {/* ALERTA INACTIVO PREMIUM */}
        {!isActivo && (
          <div className="bg-rose-50 text-rose-700 p-8 rounded-[2.5rem] flex items-center gap-6 border-2 border-rose-100 animate-pulse shadow-xl shadow-rose-900/5">
            <div className="p-4 bg-white rounded-2xl shadow-sm">
              <AlertTriangle size={32} className="text-rose-500" />
            </div>
            <div>
              <p className="text-xs font-black uppercase tracking-widest mb-1">Estatus de Cuenta: Restringido</p>
              <p className="text-xl font-black">Este socio se encuentra actualmente INACTIVO en el sistema.</p>
              <p className="text-sm font-medium opacity-70 mt-1">Todas las operaciones financieras y de crédito están bloqueadas para esta cuenta.</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-[4rem] shadow-2xl shadow-slate-900/5 border border-slate-100 overflow-hidden relative">
          {/* Design accents */}
          <div className="absolute top-0 left-0 w-2 h-full bg-uni-teal/20"></div>

          <form onSubmit={handleSubmit} className="p-16">
            <fieldset disabled={!isEditingMode} className="space-y-24">
              <section className="relative">
                <div className="absolute -left-20 top-0 text-[10px] font-black text-slate-300 uppercase tracking-[0.5em] [writing-mode:vertical-lr] rotate-180">Personal</div>
                {renderStep1()}
              </section>

              <div className="h-px bg-gradient-to-r from-transparent via-slate-100 to-transparent" />

              <section className="relative">
                <div className="absolute -left-20 top-0 text-[10px] font-black text-slate-300 uppercase tracking-[0.5em] [writing-mode:vertical-lr] rotate-180">Financiero</div>
                {renderStep2()}
              </section>

              <div className="h-px bg-gradient-to-r from-transparent via-slate-100 to-transparent" />

              <section className="relative">
                <div className="absolute -left-20 top-0 text-[10px] font-black text-slate-300 uppercase tracking-[0.5em] [writing-mode:vertical-lr] rotate-180">Relaciones</div>
                {renderStep3()}
              </section>

              <div className="h-px bg-gradient-to-r from-transparent via-slate-100 to-transparent" />

              <section className="relative">
                <div className="absolute -left-20 top-0 text-[10px] font-black text-slate-300 uppercase tracking-[0.5em] [writing-mode:vertical-lr] rotate-180">Legado</div>
                {renderStep4()}
              </section>

              <div className="h-px bg-gradient-to-r from-transparent via-slate-100 to-transparent" />

              <section className="relative">
                <div className="absolute -left-20 top-0 text-[10px] font-black text-slate-300 uppercase tracking-[0.5em] [writing-mode:vertical-lr] rotate-180">Legal</div>
                {renderStep5()}
              </section>

              {isEditingMode && (
                <div className="sticky bottom-10 z-50 mt-16 bg-slate-900 p-8 rounded-[2.5rem] shadow-2xl flex flex-col md:flex-row justify-between items-center gap-6 border border-white/10 backdrop-blur-2xl">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-uni-teal/10 rounded-xl">
                      <ShieldCheck className="w-6 h-6 text-uni-teal" />
                    </div>
                    <div className="text-left">
                      <p className="text-[10px] font-black text-white uppercase tracking-widest">Confirmación de Integridad</p>
                      <p className="text-[12px] text-slate-400 font-medium">Al guardar, se actualizarán todos los módulos del expediente.</p>
                    </div>
                  </div>
                  <button type="submit" className="w-full md:w-auto flex items-center justify-center gap-4 px-12 py-5 bg-uni-teal text-white text-[12px] font-black uppercase tracking-[0.2em] rounded-2xl hover:bg-uni-teal-dark transition-all shadow-xl hover:scale-105 active:scale-95 group">
                    <Save size={20} className="group-hover:rotate-12 transition-transform" /> Guardar Expediente Completo
                  </button>
                </div>
              )}
            </fieldset>
          </form>
        </div>
      </div>
    </div>
  );
}
